import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { publicTenantInviteAPI } from '../api';
import { useToast } from '../hooks/useToast';

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

function normalizeHexColor(raw) {
  let s = (raw || '').trim();
  if (!s) return '';
  if (!s.startsWith('#')) s = `#${s}`;
  return HEX6.test(s) ? s.toUpperCase() : '';
}

export default function TenantOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() || '';
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();

  const { data: preview, isLoading, isError, error } = useQuery({
    queryKey: ['tenant-invite-preview', token],
    queryFn: () => publicTenantInviteAPI.getPreview(token),
    enabled: Boolean(token),
    retry: false,
  });

  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [paymentMode, setPaymentMode] = useState('skip');
  const [stripeSecret, setStripeSecret] = useState('');
  const [stripeWebhook, setStripeWebhook] = useState('');
  const [sumupApiKey, setSumupApiKey] = useState('');
  const [sumupWebhook, setSumupWebhook] = useState('');
  const [aiSkip, setAiSkip] = useState(true);
  const [openaiKey, setOpenaiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!preview) return;
    setBusinessName(preview.businessName ?? '');
    setEmail(preview.email ?? '');
    setPhone(preview.phone ?? '');
    const p = normalizeHexColor(preview.primaryColor) || '#3B82F6';
    const s = normalizeHexColor(preview.secondaryColor) || '#10B981';
    setPrimaryColor(p);
    setSecondaryColor(s);
  }, [preview]);

  const accent = useMemo(() => {
    const n = normalizeHexColor(primaryColor);
    return n || '#3B82F6';
  }, [primaryColor]);

  const previewErrorMessage = isError
    ? error?.message || 'This invitation link is not valid.'
    : '';

  const goNextFromStep1 = () => {
    setLocalError('');
    if (!firstName.trim() || !lastName.trim()) {
      setLocalError('First name and last name are required.');
      return;
    }
    if (!businessName.trim()) {
      setLocalError('Business name is required.');
      return;
    }
    const em = email.trim();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setLocalError('Enter a valid email address.');
      return;
    }
    const pc = normalizeHexColor(primaryColor);
    const sc = normalizeHexColor(secondaryColor);
    if (!pc || !sc) {
      setLocalError('Primary and secondary colours must be valid hex values (e.g. #3B82F6).');
      return;
    }
    setPrimaryColor(pc);
    setSecondaryColor(sc);
    setStep(2);
  };

  const goNextFromStep2 = () => {
    setLocalError('');
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (password !== passwordConfirm) {
      setLocalError('Passwords do not match.');
      return;
    }
    setStep(3);
  };

  const goNextFromStep3 = () => {
    setLocalError('');
    if (paymentMode === 'stripe') {
      const sk = stripeSecret.trim();
      const wh = stripeWebhook.trim();
      if (!sk.startsWith('sk_test_') && !sk.startsWith('sk_live_')) {
        setLocalError('Stripe secret key should start with sk_test_ or sk_live_.');
        return;
      }
      if (!wh.startsWith('whsec_')) {
        setLocalError('Stripe webhook signing secret should start with whsec_.');
        return;
      }
    }
    if (paymentMode === 'sumup') {
      const k = sumupApiKey.trim();
      if (!/^sup_(sk|test)_[a-zA-Z0-9]+$/.test(k)) {
        setLocalError(
          'SumUp API key should start with sup_sk_ (production) or sup_test_ (sandbox).'
        );
        return;
      }
    }
    setStep(4);
  };

  const goNextFromStep4 = () => {
    setLocalError('');
    if (!aiSkip && !openaiKey.trim()) {
      setLocalError('Enter an API key or choose Skip for now.');
      return;
    }
    setStep(5);
  };

  const buildCompletePayload = () => {
    const payload = {
      token,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      businessName: businessName.trim(),
      email: email.trim(),
      phone: phone.trim() || '',
      primaryColor: normalizeHexColor(primaryColor),
      secondaryColor: normalizeHexColor(secondaryColor),
      password,
    };
    if (paymentMode === 'stripe') {
      payload.payments = {
        activeGateway: 'stripe',
        stripe: {
          secretKey: stripeSecret.trim(),
          webhookSecret: stripeWebhook.trim(),
        },
      };
    } else if (paymentMode === 'sumup') {
      const sumup = { apiKey: sumupApiKey.trim() };
      if (sumupWebhook.trim()) sumup.webhookSecret = sumupWebhook.trim();
      payload.payments = {
        activeGateway: 'sumup',
        sumup,
      };
    }
    if (!aiSkip && openaiKey.trim()) {
      payload.ai = { apiKey: openaiKey.trim() };
    }
    return payload;
  };

  const handleSubmit = async () => {
    setLocalError('');
    setSubmitting(true);
    try {
      await publicTenantInviteAPI.complete(buildCompletePayload());
      toastSuccess('Account ready', 'You can sign in with your email and password.');
      window.setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      const msg = err.message || 'Could not complete setup.';
      setLocalError(msg);
      toastError('Setup failed', msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-800 dark:text-gray-200 text-center max-w-md">
          This page needs a valid invitation link. Open the link from your email, or ask your
          administrator to resend the invitation.
        </p>
        <Link to="/login" className="text-sm text-blue-600 dark:text-blue-400 underline">
          Go to sign in
        </Link>
        <ToastContainer />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Checking your invitation…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-800 dark:text-gray-200 text-center max-w-md">{previewErrorMessage}</p>
        <Link to="/login" className="text-sm text-blue-600 dark:text-blue-400 underline">
          Go to sign in
        </Link>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4">
      <ToastContainer />
      <div className="max-w-lg mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Welcome to SparxMate</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Step {step} of 5 — set up your business account
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          {localError && (
            <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-200">
              {localError}
            </div>
          )}

          {step === 1 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Confirm your business details and how you will appear to clients. You can adjust contact
                information and brand colours here.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                    autoComplete="family-name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Business name <span className="text-red-500">*</span>
                </label>
                <input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  autoComplete="organization"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Phone
                </label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  autoComplete="tel"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Primary colour
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={normalizeHexColor(primaryColor) || '#3B82F6'}
                      onChange={(e) => setPrimaryColor(e.target.value.toUpperCase())}
                      className="h-10 w-14 rounded border border-gray-300 dark:border-gray-600 cursor-pointer bg-transparent"
                      aria-label="Primary colour"
                    />
                    <input
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-mono"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Secondary colour
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={normalizeHexColor(secondaryColor) || '#10B981'}
                      onChange={(e) => setSecondaryColor(e.target.value.toUpperCase())}
                      className="h-10 w-14 rounded border border-gray-300 dark:border-gray-600 cursor-pointer bg-transparent"
                      aria-label="Secondary colour"
                    />
                    <input
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-mono"
                      placeholder="#10B981"
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={goNextFromStep1}
                className="w-full py-2.5 rounded-md text-white text-sm font-medium"
                style={{ backgroundColor: accent }}
              >
                Continue
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Choose a password to sign in to your business account.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 p-1"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-200"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={goNextFromStep2}
                  className="flex-1 py-2.5 rounded-md text-white text-sm font-medium"
                  style={{ backgroundColor: accent }}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Connect Stripe or SumUp so your clients can pay invoices online. You can skip this and
                configure payments later in Settings.
              </p>
              <div className="space-y-2">
                {[
                  ['skip', 'Skip for now'],
                  ['stripe', 'Stripe'],
                  ['sumup', 'SumUp'],
                ].map(([value, label]) => (
                  <label
                    key={value}
                    className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="paymentMode"
                      checked={paymentMode === value}
                      onChange={() => setPaymentMode(value)}
                      className="rounded-full border-gray-300"
                    />
                    {label}
                  </label>
                ))}
              </div>
              {paymentMode === 'stripe' && (
                <div className="space-y-3 pl-1 border-l-2 border-gray-200 dark:border-gray-600">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Create keys in Stripe Dashboard → Developers → API keys and Webhooks.
                  </p>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Stripe secret key
                    </label>
                    <input
                      type="password"
                      value={stripeSecret}
                      onChange={(e) => setStripeSecret(e.target.value)}
                      placeholder="sk_test_... or sk_live_..."
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Stripe webhook signing secret
                    </label>
                    <input
                      type="password"
                      value={stripeWebhook}
                      onChange={(e) => setStripeWebhook(e.target.value)}
                      placeholder="whsec_..."
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                    />
                  </div>
                </div>
              )}
              {paymentMode === 'sumup' && (
                <div className="space-y-3 pl-1 border-l-2 border-gray-200 dark:border-gray-600">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      SumUp API key
                    </label>
                    <input
                      type="password"
                      value={sumupApiKey}
                      onChange={(e) => setSumupApiKey(e.target.value)}
                      placeholder="sup_sk_... or sup_test_..."
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      SumUp webhook secret (optional)
                    </label>
                    <input
                      type="password"
                      value={sumupWebhook}
                      onChange={(e) => setSumupWebhook(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-200"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={goNextFromStep3}
                  className="flex-1 py-2.5 rounded-md text-white text-sm font-medium"
                  style={{ backgroundColor: accent }}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Optionally add a ChatGPT / OpenAI API key for AI-assisted features (e.g. purchase
                uploads). You can add or change this later in Settings.
              </p>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                  <input
                    type="radio"
                    name="aiSkip"
                    checked={aiSkip}
                    onChange={() => {
                      setAiSkip(true);
                      setOpenaiKey('');
                    }}
                    className="rounded-full border-gray-300"
                  />
                  Skip for now
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                  <input
                    type="radio"
                    name="aiSkip"
                    checked={!aiSkip}
                    onChange={() => setAiSkip(false)}
                    className="rounded-full border-gray-300"
                  />
                  I have an OpenAI API key
                </label>
              </div>
              {!aiSkip && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    OpenAI API key
                  </label>
                  <input
                    type="password"
                    value={openaiKey}
                    onChange={(e) => setOpenaiKey(e.target.value)}
                    placeholder="sk-..."
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="flex-1 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-200"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={goNextFromStep4}
                  className="flex-1 py-2.5 rounded-md text-white text-sm font-medium"
                  style={{ backgroundColor: accent }}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {step === 5 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Review your choices and finish setup. You will sign in on the next screen.
              </p>
              <dl className="text-sm space-y-2 text-gray-800 dark:text-gray-200">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500 dark:text-gray-400">Name</dt>
                  <dd className="text-right font-medium">
                    {firstName.trim()} {lastName.trim()}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500 dark:text-gray-400">Business</dt>
                  <dd className="text-right font-medium">{businessName.trim()}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500 dark:text-gray-400">Email</dt>
                  <dd className="text-right font-medium break-all">{email.trim()}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500 dark:text-gray-400">Payments</dt>
                  <dd className="text-right font-medium">
                    {paymentMode === 'skip'
                      ? 'Not set (configure in Settings)'
                      : paymentMode === 'stripe'
                        ? 'Stripe'
                        : 'SumUp'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500 dark:text-gray-400">AI</dt>
                  <dd className="text-right font-medium">
                    {!aiSkip && openaiKey.trim() ? 'OpenAI key provided' : 'Skipped'}
                  </dd>
                </div>
              </dl>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(4)}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-200 disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleSubmit}
                  className="flex-1 py-2.5 rounded-md text-white text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: accent }}
                >
                  {submitting ? 'Saving…' : 'Complete setup'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
